const isResolvedResource = require('./is-resolved-resource');
const processError = require('./error-process');

const accumulateWantedLinks = (source, links, fieldsToResolve) => {
  if (Array.isArray(source)) {
    source.forEach((item) => {
      if (item.fields) {
        accumulateWantedLinks(item.fields, links, fieldsToResolve);
      } else if (!isResolvedResource(item)) {
        links.push({
          parent: item,
          link: item.sys
        });
      } else {
        // probably a primitive array, so nothing to do
      }
    });

    return;
  } else if (source !== null && (typeof source === 'object')) {
    Object.keys(source).forEach((key) => {
      if (fieldsToResolve && !fieldsToResolve.includes(key)) {
        // Not a field we want to resolve
        return;
      }

      if (source[key] && source[key]['nb-NO']) {
        if (!isResolvedResource(source[key]['nb-NO'])) {
          links.push({
            parent: source[key]['nb-NO'],
            link: source[key]['nb-NO'].sys
          });
        } else {
          accumulateWantedLinks(source[key]['nb-NO'], links, fieldsToResolve);
        }
      } else if (isResolvedResource(source[key])) {
        // This would only be the case on first iteration of an object?
        // e.g. we have the object with {fields: {...}, sys: {...}}
        accumulateWantedLinks(source[key], links, fieldsToResolve);
      } else {
        links.push({
          parent: source[key],
          link: source[key].sys
        });
      }
    });

    return;
  }

  // End up here for primitive types. strings/numbers etc.
};

const idsOfType = (links, type) => {
  return [...new Set( // Unique ids
    links.filter((l) => {
      return l.link.linkType === type;
    }).map((l) => {
      return l.link.id;
    })
  )];
};

const batches = (original, batchSize) => {
  const data = Array.from(original);
  const batches = [];

  while (data.length) {
    batches.push(data.slice(0, batchSize));
    data.splice(0, batchSize);
  }

  return batches;
};

const resolve = (space, object, resourceMap, fieldsToResolve) => {
  const links = [];
  // populate the links array with overview of links to resolve, and "pointers" to their posistion on the object
  // Note: We pass inn the items array an not the complete object if its a collection
  accumulateWantedLinks(object.items || object, links, fieldsToResolve);

  if (!links.length) {
    return Promise.resolve(object);
  }

  // Filter out resources we've already resolved:
  const unresolvedLinks = links.filter((l) => {
    return !resourceMap.has(l.link.id);
  });

  const entryIds = idsOfType(unresolvedLinks, 'Entry');
  const assetIds = idsOfType(unresolvedLinks, 'Asset');
  const contentTypeIds = idsOfType(unresolvedLinks, 'ContentType');

  const promises = [];

  if (entryIds.length) {
    promises.push(
      batches(entryIds, 75).reduce((current, batch) => {
        return current.then(() => {
          return space.getEntries({
            'sys.id[in]': batch.join(','),
            'limit': batch.length
          }).catch(
            processError
          ).then((response) => {
            const entries = response.toPlainObject();

            entries.items.forEach((entry) => {
              resourceMap.set(entry.sys.id, entry);
            });
          });
        });
      }, Promise.resolve())
    );
  }

  if (assetIds.length) {
    promises.push(
      batches(assetIds, 75).reduce((current, batch) => {
        return current.then(() => {
          return space.getAssets({
            'sys.id[in]': batch.join(','),
            'limit': batch.length
          }).catch(
            processError
          ).then((response) => {
            const assets = response.toPlainObject();

            assets.items.forEach((asset) => {
              resourceMap.set(asset.sys.id, asset);
            });
          });
        });
      }, Promise.resolve())
    );
  }

  if (contentTypeIds.length) {
    promises.push(
      batches(contentTypeIds, 75).reduce((current, batch) => {
        return current.then(() => {
          return space.getContentTypes({
            'sys.id[in]': batch.join(','),
            'limit': batch.length
          }).catch(
            processError
          ).then((response) => {
            const contentTypes = response.toPlainObject();

            contentTypes.items.forEach((contentType) => {
              resourceMap.set(contentType.sys.id, contentType);
            });
          });
        });
      }, Promise.resolve())
    );
  }

  return Promise.all(promises).then(() => {
    links.forEach((l) => {
      const resourceId = l.link.id;
      if (!resourceMap.has(resourceId)) {
        // Leave links to resources we haven't resolved alone:
        return;
      }

      l.parent.fields = resourceMap.get(resourceId).fields;
      l.parent.sys = resourceMap.get(resourceId).sys;
      l.parent.metadata = resourceMap.get(resourceId).metadata;
    });

    return object;
  });
};

module.exports = (space, object, levels, fieldsToResolve) => {
  let resolving = Promise.resolve(object);
  const resourceMap = new Map();

  levels = parseInt(levels, 10);

  while (levels--) {
    resolving = resolving.then((object) => {
      return resolve(space, object, resourceMap, fieldsToResolve);
    });
  }

  return resolving;
};
